import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Clock, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export const WEEKDAYS = [
  ["mon", "Segunda"], ["tue", "Terça"], ["wed", "Quarta"], ["thu", "Quinta"],
  ["fri", "Sexta"], ["sat", "Sábado"], ["sun", "Domingo"],
];
const JS_DAY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const DEFAULT_HOURS = Object.fromEntries(WEEKDAYS.map(([k]) => [k,
  k === "sun" ? { open: false, start: null, end: null }
  : k === "sat" ? { open: true, start: "08:00", end: "12:00" }
  : { open: true, start: "08:00", end: "18:00" }]));

function nowBRT() {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23", weekday: "short",
  }).formatToParts(new Date()).map((p) => [p.type, p.value]));
  const wd = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[parts.weekday];
  return { date: `${parts.year}-${parts.month}-${parts.day}`, day: JS_DAY[wd], time: `${parts.hour}:${parts.minute}` };
}

export function shopOpenState(shop) {
  const hours = shop?.business_hours;
  if (!hours) return null;
  const n = nowBRT();
  if (shop.closed_today === n.date) return { open: false, closedToday: true, today: n.day };
  const d = hours[n.day];
  const open = !!(d?.open && d.start && d.end && n.time >= d.start && n.time < d.end);
  return { open, closedToday: false, today: n.day };
}

export function ShopHoursCard({ shop }) {
  const st = shopOpenState(shop);
  if (!st) return null;
  return (
    <div data-testid="shop-hours-card" className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-[11px] font-mono-caps text-zinc-500"><Clock className="h-4 w-4" /> Horário de funcionamento</div>
        <span data-testid="shop-open-status" className={`shrink-0 whitespace-nowrap px-3 py-1 rounded-full text-xs font-bold uppercase ${st.open ? "bg-emerald-900/60 text-emerald-300 border border-emerald-700" : "bg-red-950/60 text-red-300 border border-red-800"}`}>
          {st.open ? "Aberto agora" : st.closedToday ? "Fechado hoje" : "Fechado agora"}
        </span>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {WEEKDAYS.map(([k, label]) => {
          const d = shop.business_hours[k];
          return (
            <li key={k} data-testid={`shop-hours-${k}`} className={`flex justify-between ${k === st.today ? "text-zinc-100 font-semibold" : "text-zinc-400"}`}>
              <span>{label}</span>
              <span>{d?.open ? `${d.start} – ${d.end}` : "Fechado"}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DayRow({ k, label, d, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b border-zinc-900 last:border-0">
      <div className="w-20 text-sm text-zinc-200">{label}</div>
      <Switch data-testid={`hours-open-${k}`} checked={d.open} onCheckedChange={(v) => onChange({ ...d, open: v, start: d.start || "08:00", end: d.end || "18:00" })} />
      {d.open ? (
        <div className="flex items-center gap-2">
          <Input data-testid={`hours-start-${k}`} type="time" value={d.start || ""} onChange={(e) => onChange({ ...d, start: e.target.value })} className="w-28 bg-zinc-900 border-zinc-800" />
          <span className="text-zinc-500 text-sm">até</span>
          <Input data-testid={`hours-end-${k}`} type="time" value={d.end || ""} onChange={(e) => onChange({ ...d, end: e.target.value })} className="w-28 bg-zinc-900 border-zinc-800" />
        </div>
      ) : <span className="text-sm text-zinc-500">Fechado</span>}
    </div>
  );
}

export function BusinessHoursEditor({ shop, onSaved }) {
  const [hours, setHours] = useState(shop?.business_hours || DEFAULT_HOURS);
  const [busy, setBusy] = useState(false);
  const closedToday = shopOpenState({ ...shop, business_hours: hours })?.closedToday;
  const save = async (payload, msg) => {
    setBusy(true);
    try { await api.patch(`/shops/${shop.id}/business-hours`, payload); toast.success(msg); await onSaved?.(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <section data-testid="business-hours-section" className="rounded-2xl border border-card2-border bg-appbg-soft p-5">
      <div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-amber-brand" /><h2 className="font-display text-lg font-bold text-zinc-100">Horário de funcionamento</h2></div>
      <p className="text-sm text-zinc-400 mb-3">Seus clientes veem esses horários e se a oficina está aberta agora (horário de Brasília).</p>
      {WEEKDAYS.map(([k, label]) => (
        <DayRow key={k} k={k} label={label} d={hours[k] || { open: false }} onChange={(d) => setHours({ ...hours, [k]: d })} />
      ))}
      <div className="flex flex-wrap gap-2 mt-4">
        <Button data-testid="hours-save" disabled={busy} onClick={() => save({ business_hours: hours }, "Horários salvos")} className="bg-amber-brand hover:bg-amber-brandDark text-black font-bold uppercase">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvar horários
        </Button>
        <Button data-testid="hours-closed-today" variant="outline" disabled={busy || !shop?.business_hours} onClick={() => save({ closed_today: !closedToday }, closedToday ? "Oficina reaberta hoje" : "Marcada como fechada hoje")} className="bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800">
          {closedToday ? "Reabrir hoje" : "Fechado hoje"}
        </Button>
      </div>
      {!shop?.business_hours && <p className="text-xs text-zinc-500 mt-2">Salve os horários primeiro para usar “Fechado hoje”.</p>}
      {closedToday && <p data-testid="hours-closed-today-note" className="text-xs text-red-300 mt-2">Fechada hoje até 23:59. Volta ao normal amanhã automaticamente.</p>}
    </section>
  );
}
